/**
 * What's New — release notes shown in-app
 * ────────────────────────────────────────
 * PROCESS (for every user-visible release): append a new entry to the TOP of
 * this array with a version number one higher than the current latest. The
 * overlay shows each athlete every entry they haven't seen yet, exactly once.
 *
 * Writing guidelines:
 * - Speak to the athlete, not the changelog ("Your runs now sync
 *   automatically", not "Implemented webhook ingest").
 * - 2-4 items per entry, one sentence of body each.
 * - Skip internal work (refactors, fixes for things users never saw).
 */

export interface WhatsNewItem {
  title: string;
  body: string;
}

export interface WhatsNewEntry {
  version: number;      // monotonically increasing, newest entry = highest
  date: string;         // YYYY-MM-DD
  title: string;
  items: WhatsNewItem[];
}

export const WHATS_NEW: WhatsNewEntry[] = [
  {
    version: 5,
    date: '2026-07-19',
    title: 'Your coach now reaches out first',
    items: [
      { title: 'Morning heads-up', body: 'A push at 6am with your session and brief — no more remembering to check.' },
      { title: 'Streak protection alert', body: 'If your streak is on the line at 9pm, Kader taps you on the shoulder before midnight takes it.' },
      { title: 'Midweek coach check-in', body: 'On Wednesdays your coach opens the conversation — grounded in your actual week, not a canned message.' },
      { title: 'Share your week', body: 'A share button on your weekly review turns the week into a card worth posting.' },
    ],
  },
  {
    version: 4,
    date: '2026-07-19',
    title: 'Air quality & smarter check-ins',
    items: [
      { title: 'Smoke check before you run', body: 'Kader now checks air quality alongside the weather. When wildfire smoke or pollution is bad, your brief says so and moves the session indoors with one tap.' },
      { title: 'Weather for where you are', body: 'Pace adjustments now use your actual location — enable it right from the session card.' },
      { title: 'Done and debriefed', body: 'Marking a session done now flows straight into your check-in, so your streak and your coach both hear about it.' },
    ],
  },
  {
    version: 3,
    date: '2026-07-19',
    title: 'A cleaner, clearer Kader',
    items: [
      { title: 'Your session front and center', body: 'The Today screen now leads with your session and a one-tap Mark as done.' },
      { title: 'Coach starter prompts', body: 'Not sure what to ask? The coach now suggests a few good openers.' },
      { title: 'Tidier Me tab', body: 'All your devices and sync options live in one place now.' },
    ],
  },
  {
    version: 2,
    date: '2026-07-19',
    title: 'Streaks, badges & levels',
    items: [
      { title: 'Daily streak', body: 'Every check-in — training or rest day — builds your streak. Watch the flame in the header.' },
      { title: 'Milestone badges', body: 'Hit 3, 7, 14, 30+ day streaks to unlock badges in your trophy case on Progress.' },
      { title: 'XP & levels', body: 'Showing up earns XP. Levels are the long game — a year of check-ins takes you near level 20.' },
    ],
  },
  {
    version: 1,
    date: '2026-07-19',
    title: 'Your runs, synced automatically',
    items: [
      { title: 'Strava & wearable sync', body: 'Connect once in the Me tab and every run lands in Kader by itself — no more manual logging.' },
      { title: 'Sessions auto-complete', body: 'When a synced run matches your planned session, it checks itself off.' },
      { title: 'Post-run check-in', body: 'After a run, just add how it felt and any niggles — ten seconds, done.' },
      { title: 'Weekly review, delivered', body: 'Your review now arrives Sunday evening as a notification, ready with Monday’s brief.' },
    ],
  },
];

export const LATEST_WHATS_NEW_VERSION = WHATS_NEW.reduce((m, e) => Math.max(m, e.version), 0);
